import React, { useCallback, useMemo } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { RecyclerListView, RecyclerListViewProps, DataProvider, LayoutProvider } from 'recyclerlistview';
import {
  DNABox,
  DNAButton,
  DNAText,
  DNADivider,
  GridList,
  luxColors,
} from '@alucio/lux-ui';
import DNAEmpty, { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { PromptValues } from '@alucio/aws-beacon-amplify/src/models';
import { usePromptValuesState } from 'src/state/context/PromptValues/PromptValuesStateProvider';
import PromptValueModal from './PromptValueModal';
import { useDispatch } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors['color-text-white'],
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
    minHeight: 300,
  },
  tableHeader: {
    backgroundColor: colors['color-gray-10'],
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  row: {
    flexBasis: 'auto',
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
  valueText: {
    color: colors['color-text-primary'],
    lineHeight: 20,
  },
  emptyStateContainer: {
    padding: 40,
  },
  itemWrapper: {
    // required for the recyclerlistview to work correctly
    width: '100%',
  },
});

interface PromptValuesListProps {
  onCreate: () => void;
}

interface PromptValueRowProps {
  value: PromptValues;
  index: number;
  onEdit: (value: PromptValues) => void;
  onDelete: (value: PromptValues) => void;
  columnLabel: string;
}

interface DeviceModeVariant {
  columnConfig: string;
}

type DeviceModeVariants = Record<DeviceMode, DeviceModeVariant>;

const deviceModeVariants: DeviceModeVariants = {
  desktop: {
    columnConfig: 'minmax(300px, 2fr) minmax(200px, 1fr)',
  },
  tablet: {
    columnConfig: 'minmax(250px, 2fr) minmax(180px, 1fr)',
  },
};

const PromptValueRow: React.FC<PromptValueRowProps> = ({
  value,
  index,
  onEdit,
  onDelete,
}) => {
  return (
    <GridList.Row
      style={styles.row}
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      testID={`prompt-value-row-${index}`}
    >
      <GridList.Col>
        <DNAText
          p2
          style={styles.valueText}
          testID={`prompt-value-text-${index}`}
          numberOfLines={4}
        >
          {value.value}
        </DNAText>
      </GridList.Col>

      {/* Action Buttons */}
      <GridList.Col>
        <DNABox fill spacing="sm" alignX="end">
          <DNABox appearance="row" spacing="xs">
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconRight="pencil-simple"
              onPress={() => onEdit(value)}
              testID={`prompt-value-edit-${index}`}
            />
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconRight="trash"
              onPress={() => onDelete(value)}
              testID={`prompt-value-delete-${index}`}
            />
          </DNABox>
        </DNABox>
      </GridList.Col>
    </GridList.Row>
  );
};

const PromptValuesList: React.FC<PromptValuesListProps> = ({ onCreate }) => {
  const { values, config, handleUpdate, handleDelete } = usePromptValuesState();
  const dispatch = useDispatch();
  const dimensions = useWindowDimensions();
  const { deviceMode } = useAppSettings();

  const { columnConfig } = deviceModeVariants[deviceMode];

  const handleEdit = useCallback((value: PromptValues) => {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: (modalProps) => (
        <PromptValueModal
          {...modalProps}
          onSave={(updatedText: string) => handleUpdate(value, updatedText)}
          value={value}
          config={config}
          title={config.modalTitles.edit}
        />
      ),
    }));
  }, [dispatch, handleUpdate, config]);

  const handleDeleteClick = useCallback((value: PromptValues) => {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: () => (
        <DNACommonConfirmation
          status="danger"
          cancelText="Cancel"
          confirmActionText="Delete"
          title={`Delete ${config.singularName}`}
          descriptionText={
            `Are you sure you want to delete this ${config.singularName}?\n\nThis action cannot be undone.`
          }
          onConfirmAction={() => handleDelete(value)}
        />
      ),
    }));
  }, [dispatch, handleDelete, config]);

  const dataProvider = useMemo(
    () => new DataProvider((prev: PromptValues, next: PromptValues) => prev !== next)
      .cloneWithRows(values),
    [values],
  );

  const layoutProvider = useMemo(
    () => new LayoutProvider(
      () => 'DEFAULT',
      (itemType, dim) => {
        dim.width = dimensions.width || 400;
        dim.height = 58;
      },
    ),
    [dimensions.width],
  );

  const rowRenderer = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, value: PromptValues, index) => {
      return (
        <React.Fragment key={value.id}>
          <PromptValueRow
            value={value}
            index={index}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            columnLabel={config.singularName}
          />
          <DNADivider />
        </React.Fragment>
      );
    },
    [handleEdit, handleDeleteClick, config],
  );

  const renderItemWrapper = useCallback<NonNullable<RecyclerListViewProps['renderItemContainer']>>(
    (props, _parentProps, children) => {
      const { style, ...otherProps } = props as { style?: any; [key: string]: any };
      return (
        <DNABox {...otherProps} style={{ ...style, ...styles.itemWrapper }}>
          {children}
        </DNABox>
      );
    },
    [],
  );

  if (values.length === 0) {
    return (
      <DNABox fill>
        <DNAEmpty
          emptyVariant={EmptyVariant.CustomLabelEmpty}
          customEmptyLabel={config.emptyState.label}
          customEmptyActionLabel={config.emptyState.actionLabel}
          customEmptyOnPress={onCreate}
        />
      </DNABox>
    );
  }

  return (
    <DNABox style={styles.container} appearance="col" fill>
      <GridList
        cols={columnConfig}
        gap="12px"
      >
        {/* Table Header */}
        <GridList.Header style={styles.tableHeader}>
          <GridList.Col>
            <DNAText b1>{config.singularName}</DNAText>
          </GridList.Col>
          <GridList.Col>
            <DNABox alignX="end" fill >
              <DNAText b1>Actions</DNAText>
            </DNABox>
          </GridList.Col>
        </GridList.Header>

        {/* Values List */}
        <RecyclerListView
          style={{
            flex: 1,
            minHeight: 250,
          }}
          layoutProvider={layoutProvider}
          dataProvider={dataProvider}
          rowRenderer={rowRenderer}
          renderAheadOffset={dimensions.height}
          canChangeSize
          forceNonDeterministicRendering
          renderItemContainer={renderItemWrapper}
          testID="prompt-values-recyclerlistview"
        />
      </GridList>
    </DNABox>
  );
};

export default PromptValuesList;
